import {
  HttpBackend,
  provideHttpClient,
  withInterceptors,
  withInterceptorsFromDi,
} from '@angular/common/http';
import {
  ApplicationConfig,
  ENVIRONMENT_INITIALIZER,
  importProvidersFrom,
  inject,
  LOCALE_ID,
  provideBrowserGlobalErrorListeners,
} from '@angular/core';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { provideRouter, TitleStrategy } from '@angular/router';
import { SharedAuthModule } from '@arpes-is/angular-20-auth';
import { SharedLocalAppsConfigModule } from '@arpes-is/angular-20-shared-utils';
import { SharedSpinnerModule } from '@arpes-is/angular-20-spinner';
import { TranslateLoader, TranslateModule, TranslateService } from '@ngx-translate/core';
import { MultiTranslateHttpLoader } from 'ngx-translate-multi-http-loader';
import { MessageService } from 'primeng/api';
import { providePrimeNG } from 'primeng/config';

import { environment } from '../environments/environment';
import { SHARED_APP_ENVIRONMENT } from '../shared/apps-config/apps-config.token';
import { errorInterceptor } from '../shared/guards-interceptors/error.interceptor';
import { PRIMENG_IT_TRANSLATION } from '../shared/themes/primeng-it';
import NovafitoPreset from '../shared/themes/themes';
import { TranslateStoreService } from '../shared/utils/translate-store.service';

import { routes } from './app.routes';
import { PageTitleStrategy } from '../shared/title/page-title.strategy';

export function i18nLoaderFactory(httpBackend: HttpBackend): TranslateLoader {
  return new MultiTranslateHttpLoader(httpBackend, [
    { prefix: '/assets/i18n/agrochemicals/core/', suffix: '.json' },
    { prefix: '/assets/i18n/agrochemicals/agrochemicals/', suffix: '.json' },
    { prefix: '/assets/i18n/shared/', suffix: '.json' },
  ]);
}

/** Forma attesa da SharedLocalAppsConfigService delle librerie @arpes-is (come nel QdC). */
const arpesAppConfig = {
  shared: { shared: environment.shared },
  global: {},
  application: environment.application,
};

export const appConfig: ApplicationConfig = {
  providers: [
    provideBrowserGlobalErrorListeners(),
    // errorInterceptor esterno; dentro, l'AuthInterceptor della libreria (token dal sessionStorage, 401 → sessione).
    provideHttpClient(withInterceptors([errorInterceptor]), withInterceptorsFromDi()),
    {
      provide: SHARED_APP_ENVIRONMENT,
      useValue: environment.shared,
    },
    importProvidersFrom(
      SharedLocalAppsConfigModule.forRoot(arpesAppConfig),
      SharedSpinnerModule,
      SharedAuthModule,
    ),
    MessageService,
    {
      provide: LOCALE_ID,
      deps: [TranslateStoreService],
      useFactory: (translateService: TranslateStoreService) => translateService.getLanguage,
    },
    provideAnimationsAsync(),
    provideRouter(routes),
    { provide: TitleStrategy, useClass: PageTitleStrategy },
    providePrimeNG({
      ripple: false,
      translation: PRIMENG_IT_TRANSLATION,
      theme: {
        preset: NovafitoPreset,
        options: {
          darkModeSelector: '.app-dark',
        },
      },
    }),
    importProvidersFrom(
      TranslateModule.forRoot({
        defaultLanguage: 'it',
        loader: {
          provide: TranslateLoader,
          useFactory: i18nLoaderFactory,
          deps: [HttpBackend],
        },
      }),
    ),
    {
      provide: ENVIRONMENT_INITIALIZER,
      multi: true,
      useValue: () => {
        const translate = inject(TranslateService);
        translate.setDefaultLang('it');
        void translate.use('it');
      },
    },
  ],
};
